/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import { Grid, Typography, Typography2 } from '@coursera/cds-core';

import type {
  UniversityProgramDegreeDescriptionPageTuitionGridLinksLinks as GridLinksItemsType,
  UniversityProgramDegreeDescriptionPageTuitionGridLinks as GridLinksType,
  MarketingContent,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import GridLinksCard from 'bundles/degree-description/components/modules/grid-links/GridLinksCard';
import {
  filterRichTextContentByNodeType,
  isEmptyChild,
  renderRichText,
} from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import Container from 'bundles/premium-hub/components/shared/Container';

type GridLinksModuleProps = {
  gridLinkText: GridLinksType['json'];
  gridLinkItems: GridLinksItemsType;
};

const styles = {
  header: css`
    margin-bottom: 24px;
  `,
  paragraph: css`
    margin-bottom: 32px;
  `,
  wrap: css`
    margin-bottom: 48px;
    margin-top: 48px;
  `,
  cardHeight: css`
    display: flex;
    height: auto;
  `,
};

const getRichTextRenderOptions = (links: Partial<GridLinksItemsType>): Options => {
  return {
    renderNode: {
      [BLOCKS.HEADING_3]: (_, children) => {
        const theme = useDegreeDescriptionPageTheme();
        if (isEmptyChild(children)) return null;
        return (
          <Grid item xs={12}>
            <Typography variant="h1semibold" component="h3" css={[styles.header, theme.mobileFontSizeStyle?.header]}>
              {children}
            </Typography>
          </Grid>
        );
      },
      [BLOCKS.PARAGRAPH]: (_, children) => {
        const theme = useDegreeDescriptionPageTheme();
        if (isEmptyChild(children)) return null;
        return (
          <Grid item xs={12}>
            <Typography2
              variant="bodyPrimary"
              component="div"
              css={[styles.paragraph, theme.mobileFontSizeStyle?.body]}
            >
              {children}
            </Typography2>
          </Grid>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // Only Pathway items are rendered from here
        const id = node?.data?.target?.sys?.id;
        const entry = links?.entries?.block.find((link) => link?.sys?.id === id) as MarketingContent;
        if (entry && entry?.__typename === 'MarketingContent') {
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} css={styles.cardHeight}>
              <GridLinksCard
                key={entry?.sys?.id}
                title={entry?.title}
                description={entry?.description}
                link={entry?.callToActionLink}
              />
            </Grid>
          );
        }
        return null;
      },
    },
  };
};

const GridLinksModule = ({ gridLinkText, gridLinkItems }: GridLinksModuleProps) => {
  const richTextRenderOptions = getRichTextRenderOptions(gridLinkItems);
  return (
    <Container>
      <Grid container spacing={24} css={styles.wrap}>
        {renderRichText({
          content: filterRichTextContentByNodeType(gridLinkText, [
            BLOCKS.HEADING_3,
            BLOCKS.PARAGRAPH,
            BLOCKS.EMBEDDED_ENTRY,
          ]),
          richTextRenderOptions,
        })}
      </Grid>
    </Container>
  );
};

export default GridLinksModule;
